"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";

interface StaffInfo {
  id: string;
  name: string;
}

interface SubscriptionUsageDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    customers: Array<{
      customerId: string;
      customerName: string;
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    };
  };
}

const COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#6366f1", "#ec4899", "#14b8a6"];

export function SubscriptionUsageDashboard({
  data,
}: SubscriptionUsageDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);

  const handleExport = () => {
    const exportData = data.customers.map((row) => {
      const rowData: Record<string, string | number> = {
        "Customer Name": row.customerName,
      };
      staffNames.forEach((name) => {
        const staffData = row.byStaff[name] || { outside: 0, inside: 0 };
        rowData[`${name} - Outside`] = staffData.outside;
        rowData[`${name} - Inside`] = staffData.inside;
      });
      rowData["Total Outside"] = row.totalOutside;
      rowData["Total Inside"] = row.totalInside;
      return rowData;
    });

    // Add totals row
    const totalsRow: Record<string, string | number> = { "Customer Name": "TOTAL" };
    staffNames.forEach((name) => {
      const staffData = data.totals.byStaff[name] || { outside: 0, inside: 0 };
      totalsRow[`${name} - Outside`] = staffData.outside;
      totalsRow[`${name} - Inside`] = staffData.inside;
    });
    totalsRow["Total Outside"] = data.totals.totalOutside;
    totalsRow["Total Inside"] = data.totals.totalInside;
    exportData.push(totalsRow);

    exportToExcel(exportData, "Subscription_Usage", "Usage");
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Outside Washes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totals.totalOutside}</div>
            <p className="text-xs text-muted-foreground">
              All staff combined
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Inside Washes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totals.totalInside}</div>
            <p className="text-xs text-muted-foreground">
              All staff combined
            </p>
          </CardContent>
        </Card>

        {staffNames.slice(0, 2).map((name, index) => {
          const staffData = data.totals.byStaff[name] || { outside: 0, inside: 0 };
          return (
            <Card key={name}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{name} Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  style={{ color: COLORS[index % COLORS.length] }}
                >
                  {staffData.outside + staffData.inside}
                </div>
                <p className="text-xs text-muted-foreground">
                  Out: {staffData.outside} | In: {staffData.inside}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Subscription Usage by Customer</CardTitle>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2}>Customer Name</TableHead>
                  {staffNames.map((name) => (
                    <TableHead key={name} colSpan={2} className="text-center border-l">
                      {name}
                    </TableHead>
                  ))}
                  <TableHead className="text-center border-l">Total Out</TableHead>
                  <TableHead className="text-center">Total In</TableHead>
                </TableRow>
                <TableRow>
                  {staffNames.map((name) => (
                    <>
                      <TableHead key={`${name}-outside`} className="text-right border-l">Outside</TableHead>
                      <TableHead key={`${name}-inside`} className="text-right">Inside</TableHead>
                    </>
                  ))}
                  <TableHead className="text-right border-l">-</TableHead>
                  <TableHead className="text-right">-</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={staffNames.length * 2 + 3} className="text-center py-8">
                      No subscription usage data found
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.customers.map((customer) => (
                      <TableRow key={customer.customerId}>
                        <TableCell className="font-medium">
                          {customer.customerName}
                        </TableCell>
                        {staffNames.map((name) => {
                          const staffData = customer.byStaff[name] || { outside: 0, inside: 0 };
                          return (
                            <>
                              <TableCell key={`${name}-outside`} className="text-right border-l">
                                {staffData.outside}
                              </TableCell>
                              <TableCell key={`${name}-inside`} className="text-right">
                                {staffData.inside}
                              </TableCell>
                            </>
                          );
                        })}
                        <TableCell className="text-right border-l font-medium">
                          {customer.totalOutside}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {customer.totalInside}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>TOTAL</TableCell>
                      {staffNames.map((name) => {
                        const staffData = data.totals.byStaff[name] || { outside: 0, inside: 0 };
                        return (
                          <>
                            <TableCell key={`${name}-outside`} className="text-right border-l">
                              {staffData.outside}
                            </TableCell>
                            <TableCell key={`${name}-inside`} className="text-right">
                              {staffData.inside}
                            </TableCell>
                          </>
                        );
                      })}
                      <TableCell className="text-right border-l">
                        {data.totals.totalOutside}
                      </TableCell>
                      <TableCell className="text-right">
                        {data.totals.totalInside}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
